'use client';

import { useEffect, useRef, useState, type PointerEvent } from 'react';
import { cn } from '@upzi/web-shell/cn';
import LocationIcon from '@upzi/web-shell/icons/location-icon';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@upzi/web-shell/ui/popover';

type JobLocationsPopoverProps = {
  locations: string[];
  displayedLocation: string;
  fullLocation: string;
  className?: string;
};

export function JobLocationsPopover({
  locations,
  displayedLocation,
  fullLocation,
  className,
}: JobLocationsPopoverProps) {
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelClose = () => {
    if (closeTimer.current !== null) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };

  useEffect(() => cancelClose, []);

  const canHover = (event: PointerEvent<HTMLElement>) =>
    event.pointerType === 'mouse' &&
    window.matchMedia('(min-width: 1024px) and (hover: hover)').matches;

  const handlePointerEnter = (event: PointerEvent<HTMLElement>) => {
    cancelClose();
    if (canHover(event)) setOpen(true);
  };

  const handlePointerLeave = (event: PointerEvent<HTMLElement>) => {
    if (!canHover(event)) return;
    cancelClose();
    closeTimer.current = setTimeout(() => {
      closeTimer.current = null;
      setOpen(false);
    }, 150);
  };

  return (
    <>
      <span className={cn('min-w-0 truncate', expanded && 'hidden lg:inline')}>
        {displayedLocation}
      </span>
      {expanded ? (
        <span className="min-w-0 break-words lg:hidden">{fullLocation}</span>
      ) : null}
      {locations.length > 1 ? (
        <Popover
          open={open}
          onOpenChange={nextOpen => {
            cancelClose();
            if (!window.matchMedia('(min-width: 1024px)').matches) {
              setExpanded(true);
              setOpen(false);

              return;
            }
            setOpen(nextOpen);
          }}
        >
          <PopoverTrigger
            type="button"
            aria-label={`Xem tất cả ${locations.length} địa điểm làm việc`}
            className={cn(
              'bg-surface-variant focus-visible:ring-primary inline-flex h-5 shrink-0 items-center rounded-full px-1 focus-visible:ring-2 focus-visible:outline-none',
              expanded && 'hidden lg:inline-flex',
              className
            )}
            onPointerEnter={handlePointerEnter}
            onPointerLeave={handlePointerLeave}
          >
            +{locations.length - 1}
          </PopoverTrigger>
          <PopoverContent
            align="start"
            aria-label="Địa chỉ công ty"
            className="font-body bg-surface-pure text-on-surface border-base-border shadow-search-suggestions text-ui-base hidden w-[360px] max-w-[calc(100vw-2rem)] rounded-[14px] p-0 leading-5 font-normal tracking-normal lg:block"
            onOpenAutoFocus={event => event.preventDefault()}
            onPointerEnter={handlePointerEnter}
            onPointerLeave={handlePointerLeave}
          >
            <p className="px-4 pt-4 font-semibold">Địa chỉ công ty</p>
            <ul className="m-4 max-h-60 space-y-2 overflow-y-auto break-words">
              {locations.map((location, index) => (
                <li
                  key={`${index}-${location}`}
                  className="flex items-start gap-2"
                >
                  <LocationIcon
                    size={20}
                    className="text-on-surface-variant shrink-0"
                    aria-hidden
                  />
                  <span className="min-w-0">{location}</span>
                </li>
              ))}
            </ul>
          </PopoverContent>
        </Popover>
      ) : null}
    </>
  );
}
